import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "antd";
import { Cookies } from "react-cookie";
import useWebSocket from "react-use-websocket";
import { useParams } from "react-router-dom";
import AreaChart from "../../../components/charts/charts/AreaChart";
import Trades from "./Trades";
import { GetAccountProfitByDateRangeApi } from "../../../api/request/account.js";

import Deposit from "../../../assets/user/Deposit";
import Withdraw from "../../../assets/user/Withdraw";
import OpenPL from "../../../assets/user/OpenPL";
import ClosePL from "../../../assets/user/ClosePL";
import Earning from "../../../assets/user/Earning";
import InOut from "../../../assets/user/InOut";
import RMS from "../../../assets/user/RMS";
import OpenTrades from "../../../assets/user/OpenTrades";
import CloseTrades from "../../../assets/user/CloseTrades";
import TotalTrades from "../../../assets/user/TotalTrades";
import Commission from "../../../assets/user/Commission";
import dayjs from "dayjs";
// import { presets } from "../../../utils/RangePickerPresets.js";

const { RangePicker } = DatePicker;

const AccountDetails = () => {
  const { loginId } = useParams();
  const cookies = new Cookies();

  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--theme")
    ?.trim();

  const [socketData, setSocketData] = useState(null);
  const [depositWithdrawDateRange, setDepositWithdrawDateRange] = useState({ fromDate: '', toDate: '' });
  const [chartData, setChartData] = useState({
    categories: [],
    series: [{ name: "Profit", data: [] }],
  });
  const [rangePickerValue, setRangePickerValue] = useState([]);


  const disabled10DaysDate = (current, { from }) => {
    if (from) {
      return Math.abs(current.diff(from, 'days')) >= 10;
    }
    return false;
  };


  const handleDateChange = (value) => {
    if (value && value.length === 2) {
      const fromDate = value[0].format('DD-MM-YYYY');
      const toDate = value[1].format('DD-MM-YYYY');
      setDepositWithdrawDateRange({ fromDate: fromDate, toDate: toDate });
      setRangePickerValue(value);

    }
    if (!value || value.length === 0) {
      setDepositWithdrawDateRange({ fromDate: '', toDate: '' });
      setRangePickerValue([]);

    }
  };

  const fetchChartData = async () => {
    try {
      const { data } = await GetAccountProfitByDateRangeApi(loginId, {
        FromDate: depositWithdrawDateRange?.fromDate,
        ToDate: depositWithdrawDateRange?.toDate,
      });

      if (data?.success && data?.data) {
        // Format date strings to "MMM D" like "Jul 20"
        const formattedDates = data.data.date.map((d) =>
          dayjs(d, "DD-MM-YYYY").format("MMM D")
        );

        setChartData({
          categories: formattedDates,
          series: [{ name: "Profit", data: data.data.profit }],
        });
      }
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    }
  };

  useEffect(() => {
    const fromDate = dayjs().subtract(9, 'day');
    const toDate = dayjs();

    const formattedFrom = fromDate.format('DD-MM-YYYY');
    const formattedTo = toDate.format('DD-MM-YYYY');

    setDepositWithdrawDateRange({
      fromDate: formattedFrom,
      toDate: formattedTo
    });

    setRangePickerValue([fromDate, toDate]); // Set moment objects here
  }, []);


  useEffect(() => {
    fetchChartData();
  }, [depositWithdrawDateRange, loginId]);


  const { sendJsonMessage, lastJsonMessage } = useWebSocket(
    `wss://backend.rimglobal.trade/api/MTFiveAccount/ws?authorization=Bearer ${cookies.get("token")}`,
    { shouldReconnect: () => true, share: true }
  );

  useEffect(() => {
    sendJsonMessage({
      eventType: "TradeAccountOverview",
      action: "subscribe",
      loginId: loginId,
    });

    return () => {
      sendJsonMessage({
        eventType: "TradeAccountOverview",
        action: "unsubscribe",
        loginId: loginId,
      });
    };
  }, [loginId]);

  useEffect(() => {
    if (lastJsonMessage?.eventType === "TradeAccountOverview") {
      setSocketData(lastJsonMessage?.data?.Data?.[0]);
    }
  }, [lastJsonMessage]);

  const transactionCards = [
    {
      title: "Open P/L",
      value: socketData?.OpenTradeProfit.toFixed(2) || 0,
      bgColor: "bg-[#E6F1F3]",
      icon: <OpenPL color="#2dc7e3" size={7} />,
    },
    {
      title: "Closed P/L",
      value: socketData?.CloseTradeProfit.toFixed(2) || 0,
      bgColor: "bg-[#E9E7FD]",
      icon: <ClosePL color="#6158bb" size={7} />,
    },
    {
      title: "Current Equity",
      value: socketData?.CurrentEquity.toFixed(2) || 0,
      bgColor: "bg-[#DDF6E8]",
      icon: <Earning color="#28C76F" size={7} />,
    },
    {
      title: "Deposit",
      value: socketData?.Deposit.toFixed(2) || 0,
      bgColor: "bg-[#D6F4F8]",
      icon: <Deposit color="#00BAD1" size={7} />,
    },
    {
      title: "Withdraw",
      value: socketData?.Withdraw.toFixed(2) || 0,
      bgColor: "bg-[#FFF0E1]",
      icon: <Withdraw color="#FF9F43" size={7} />,
    },
  ];

  const earningCards = [
    {
      title: "In | Out",
      value: socketData?.InOut.toFixed(2) || 0,
      prefix: "$",
      bgColor: "bg-[#E6F1F3]",
      icon: <InOut color="#2dc7e3" size={7} />,
    },
    {
      title: "RMS",
      value: socketData?.RMSAmount.toFixed(2) || 0,
      prefix: "$",
      bgColor: "bg-[#E9E7FD]",
      icon: <RMS color="#6158bb" size={7} />,
    },
    {
      title: "Total Lots",
      value: socketData?.TotalLots || 0,
      bgColor: "bg-[#DDF6E8]",
      icon: <Commission color="#28C76F" size={7} />,
    },
    {
      title: "Open Trades",
      value: socketData?.OpenTrades || 0,
      bgColor: "bg-[#D6F4F8]",
      icon: <OpenTrades color="#00BAD1" size={7} />,
    },
    {
      title: "Closed Trades",
      value: socketData?.CloseTrades || 0,
      bgColor: "bg-[#FFF0E1]",
      icon: <CloseTrades color="#FF9F43" size={7} />,
    },
    {
      title: "Total Trades",
      value: socketData?.TotalTrades || 0,
      bgColor: "bg-[#FFE2E3]",
      icon: <TotalTrades color="#FF4C51" size={7} />,
    },
  ];

  const localPresets = [
    {
      label: 'Last 10 Days',
      value: [dayjs().add(-9, 'd'), dayjs()],
    },
  ];


  const memoizedChart = useMemo(() => {
    return <AreaChart data={chartData} colors={[primaryColor]} />;
  }, [chartData, primaryColor]);

  return (
    <div>
      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-5 mb-5">
        {transactionCards.map((card, index) => (
          <div
            className="flex items-center justify-between border-2 border-light rounded-[10px] p-5"
            key={index}
          >
            <div>
              <p className="sm:text-xl text-md">
                ${card.value}
              </p>
              <p className="text-sm font-medium text-gray-500 capitalize">{card.title}</p>
            </div>
            <div
              className={`flex justify-center items-center text-2xl w-12 h-12 rounded-lg ${card.bgColor}`}
            >
              {card.icon}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-5">
          {earningCards.map((card, index) => (
            <div
              className="flex flex-col items-center justify-center gap-3 border-2 border-light rounded-[10px] p-5"
              key={index}
            >
              <div
                className={`flex justify-center items-center text-2xl w-12 h-12 rounded-lg mb-2 ${card.bgColor}`}
              >
                {card.icon}
              </div>
              <div className="text-center">
                <p className="sm:text-xl text-md">
                  {card.prefix}
                  {card.value}
                </p>
                <p className="text-sm font-medium text-gray-500 capitalize">{card.title}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="border-2 border-light rounded-[10px] p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
            <h2 className="text-title font-semibold">Profit</h2>
            <RangePicker disabledDate={disabled10DaysDate} className='py-1' onChange={(value) => handleDateChange(value)} value={rangePickerValue} presets={localPresets} format='DD-MM-YYYY' />
          </div>
          {memoizedChart}
        </div>
      </div>

      <div className="mt-5">
        <Trades />
      </div>
    </div>
  );
};

export default AccountDetails;
